import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../components/ui/Button';
import CenterMessage from '../components/ui/CenterMessage';
import ScoreRadioGroup from '../components/ui/ScoreRadioGroup';
import DisclosureBadge from '../components/epr/DisclosureBadge';
import { useToast } from '../components/ui/Toast';
import { useAutoSave } from '../hooks/useAutoSave';
import {
  COMPANY_VALUE_KEYS, COMPANY_VALUE_NAMES,
  type CompanyValueKey, type Review360Submission,
  type Score1to5, type Review360Relationship,
} from '../types/eprFramework';
import type { MagicLinkToken } from '../types/magicLinkToken';
import {
  openSubmission, patchSubmission, finaliseSubmission, Review360Error,
} from '../services/review360SubmissionService';
import { markTokenUsed, MagicLinkError } from '../services/magicLinkService';
import RecipientBanner from '../components/magic/RecipientBanner';
import FormHeaderCard from '../components/magic/FormHeaderCard';

/**
 * Review360MagicForm — magic-link page for Step 2 (360 review).
 *
 * Disclosure rule (CLAUDE.md §D.1): badge shown at top before any field is
 * filled. Wording fixed per visibility:
 *   - 'attributed' → identity will be visible to employee + manager
 *   - 'anonymous'  → feedback is anonymous
 */

const VALUE_LEGEND: Readonly<Record<number, string>> = {
  1: 'Below Standard',
  2: 'Needs Improvement',
  3: 'Meets Expectation',
  4: 'Strong',
  5: 'Role Model',
};

const VALUE_DESCRIPTIONS: Record<CompanyValueKey, string> = {
  take_ownership:         'Owns commitments, supports team delivery, protects company outcomes.',
  be_progressive:         'Improves how work gets done, adapts constructively, helps things move forward.',
  apply_transparency:     'Communicates clearly, shares info, raises risks early.',
  encourage_ideas:        'Contributes useful ideas, welcomes different views, turns ideas into action.',
  empower_community:      'Builds trust, works across teams, supports colleagues.',
  practice_sustainability:'Acts responsibly with resources, safety, environment, long-term impact.',
};

const RELATIONSHIP_LABELS: Record<Review360Relationship, string> = {
  manager:     'Manager',
  peer:        'Peer',
  subordinate: 'Subordinate / Direct report',
};

interface Review360MagicFormProps {
  token: MagicLinkToken;
}

const Review360MagicForm: React.FC<Review360MagicFormProps> = ({ token }) => {
  const toast = useToast();
  const save = useAutoSave();

  const [submission, setSubmission] = useState<Review360Submission | null>(null);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState<string | null>(null);
  const [done, setDone]             = useState(false);
  const [submitInFlight, setSubmitInFlight] = useState(false);
  const [expiredMid, setExpiredMid] = useState(false);

  const visibility = token.effectiveVisibility ?? 'anonymous';
  const relationship = useMemo<Review360Relationship>(() => {
    if (token.recipientRole === 'manager' || token.recipientRole === 'peer' || token.recipientRole === 'subordinate') {
      return token.recipientRole;
    }
    return 'peer';
  }, [token.recipientRole]);

  // ─── Load / lazy create ─────────────────────────────────────────
  //
  // Deps are the stable id-bits of the token, NOT the token object
  // itself — when the parent re-creates the token on every render
  // (common with router state) a whole-object dep refires the effect
  // and hits Firestore on every paint. The `alive` flag handles
  // out-of-order completion, but reducing redundant fetches matters
  // for anonymous magic-link traffic (rate-limit budget).
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const sub = await openSubmission({
          token,
          submissionId: token.targetDocId,
          cycleId: token.cycleId,
          revieweeId: token.revieweeId ?? deriveRevieweeFromToken(token),
          relationship,
          effectiveVisibility: visibility,
          reviewerId: token.recipientId,
          reviewerName: token.recipientName,
        });
        if (!alive) return;
        setSubmission(sub);
      } catch (e) {
        if (!alive) return;
        setLoadError((e as Error).message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token.id, token.targetDocId, token.cycleId, relationship, visibility]);

  // ─── Local state mirrors ───────────────────────────────────────
  const [scores, setScores] = useState<Partial<Record<CompanyValueKey, Score1to5>>>({});
  const [strengths, setStrengths] = useState('');
  const [improve, setImprove]     = useState('');
  const [overall, setOverall]     = useState('');

  useEffect(() => {
    if (!submission) return;
    setScores(submission.valueScores ?? {});
    setStrengths(submission.strengths ?? '');
    setImprove(submission.thingsToImprove ?? '');
    setOverall(submission.overallComments ?? '');
  }, [submission]);

  // ─── Debounced patch ────────────────────────────────────────────
  const tRef = useRef<number | null>(null);
  const queuePatch = useCallback(
    (fn: () => Promise<void>, ms = 800) => {
      if (tRef.current) window.clearTimeout(tRef.current);
      tRef.current = window.setTimeout(async () => {
        save.markSaving();
        try {
          await fn();
          save.markSaved();
        } catch (e) {
          const msg = (e as Error).message;
          save.markError(msg);
          if (e instanceof Review360Error && e.code === 'wrong_state') {
            setExpiredMid(true);
            toast.show('Form is locked. Draft saved up to the last successful save.', { icon: 'warning' });
          } else {
            toast.show(`Save failed: ${msg}`, { icon: 'error' });
          }
        }
      }, ms);
    },
    [save, toast],
  );

  const handleScore = useCallback((key: CompanyValueKey, score: Score1to5) => {
    setScores(prev => ({ ...prev, [key]: score }));
    if (!submission) return;
    queuePatch(() => patchSubmission(submission.id, { valueScore: { key, score } }), 200);
  }, [submission, queuePatch]);

  const handleStrengths = useCallback((v: string) => {
    setStrengths(v);
    if (!submission) return;
    queuePatch(() => patchSubmission(submission.id, { strengths: v }));
  }, [submission, queuePatch]);

  const handleImprove = useCallback((v: string) => {
    setImprove(v);
    if (!submission) return;
    queuePatch(() => patchSubmission(submission.id, { thingsToImprove: v }));
  }, [submission, queuePatch]);

  const handleOverall = useCallback((v: string) => {
    setOverall(v);
    if (!submission) return;
    queuePatch(() => patchSubmission(submission.id, { overallComments: v }));
  }, [submission, queuePatch]);

  // ─── Submit ────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!submission) return;
    setSubmitInFlight(true);
    try {
      await finaliseSubmission(submission.id);
      await markTokenUsed(token.id);
      setDone(true);
    } catch (e) {
      const err = e as Review360Error | MagicLinkError | Error;
      if (err instanceof MagicLinkError && err.code === 'expired') {
        setExpiredMid(true);
        toast.show('Your link has expired.', { icon: 'warning' });
      } else {
        toast.show(err.message || 'Submit failed', { icon: 'error' });
      }
    } finally {
      setSubmitInFlight(false);
    }
  }, [submission, token.id, toast]);

  // ─── Terminal states ───────────────────────────────────────────
  if (loading) return <CenterMessage tone="info" title="Loading..." body="Preparing your 360 review form..." />;
  if (loadError || !submission) {
    return (
      <CenterMessage
        tone="error"
        title="Cannot open form"
        body={loadError ?? 'No form exists for this link.'}
      />
    );
  }
  if (done) {
    return (
      <CenterMessage
        tone="success"
        title="Thank you"
        body="Your feedback has been received."
      />
    );
  }

  const isInteractive = !expiredMid;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-stone-500">
              Step 2 — 360 Review
            </div>
            <div className="text-base font-semibold text-stone-900 truncate">
              {token.revieweeName
                ? <>Reviewing {token.revieweeName} — as {RELATIONSHIP_LABELS[relationship]}</>
                : <>As {RELATIONSHIP_LABELS[relationship]}</>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {visibility === 'anonymous' ? (
              <DisclosureBadge
                icon="visibility_off"
                label="Your feedback is anonymous"
                tone="success"
              />
            ) : (
              <DisclosureBadge
                icon="visibility"
                label="Your identity will be shown"
                tone="warning"
              />
            )}
            <DisclosureBadge
              icon={save.state.status === 'saving' ? 'sync' :
                    save.state.status === 'error' ? 'error' :
                    save.state.status === 'saved' ? 'check_circle' : 'cloud_done'}
              label={save.label ?? 'Auto-save on'}
              tone={save.state.status === 'error' ? 'warning' :
                    save.state.status === 'saved' ? 'success' : 'info'}
            />
          </div>
        </div>
        {expiredMid && (
          <div className="bg-amber-50 border-t border-amber-200">
            <div className="max-w-3xl mx-auto px-4 py-2 text-sm text-amber-900">
              Link expired. Form is now read-only. Draft saved up to the last successful save.
            </div>
          </div>
        )}
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6 pb-32">
        {/* Identity banner — catches accidental email forwards. */}
        <RecipientBanner
          recipientEmail={token.recipientEmail}
          recipientName={token.recipientName}
        />
        {/* Canonical form header (360 Review Fom.docx) — auto-filled from
            the token snapshot; the reviewer types none of it. Shown only
            to the reviewer; reviewer identity on employee/manager surfaces
            stays governed by §D.1. */}
        <FormHeaderCard
          title="360 Review"
          rows={[
            { label: 'Employee', value: token.revieweeName },
            { label: 'Relationship', value: RELATIONSHIP_LABELS[relationship] },
            { label: 'Reviewer', value: token.recipientName ?? token.recipientEmail },
            { label: 'Review Date', value: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) },
          ]}
        />
        <div className="rounded-xl border border-stone-200 bg-white p-5 text-sm text-stone-700 space-y-2 leading-relaxed">
          <p>
            Answer honestly based on observed behaviour. If you haven't worked closely enough
            with this person to judge a value, leave it blank.
          </p>
          <p className="text-stone-500">
            Scores of 5 or ≤ 2 should be supported with concrete examples in the notes below.
          </p>
        </div>

        {COMPANY_VALUE_KEYS.map(key => (
          <ScoreRadioGroup
            key={key}
            label={COMPANY_VALUE_NAMES[key]}
            description={VALUE_DESCRIPTIONS[key]}
            value={scores[key]}
            onChange={s => handleScore(key, s as Score1to5)}
            legend={VALUE_LEGEND}
            disabled={!isInteractive}
          />
        ))}

        <Textarea
          label="Strengths"
          description="What does this employee do well? Give specific examples where possible."
          value={strengths}
          onChange={handleStrengths}
          disabled={!isInteractive}
        />
        <Textarea
          label="Things to improve"
          description="What could be improved or developed? Focus on behaviour, not personality."
          value={improve}
          onChange={handleImprove}
          disabled={!isInteractive}
        />
        <Textarea
          label="Overall comments"
          description="General feedback, observations, or other suggestions for development."
          value={overall}
          onChange={handleOverall}
          disabled={!isInteractive}
        />
      </main>

      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-sm text-stone-600 truncate">
            {save.label ?? 'Changes saved automatically'}
          </div>
          <Button
            variant="submit"
            icon="send"
            disabled={!isInteractive}
            loading={submitInFlight}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>
      </footer>
    </div>
  );
};

const Textarea: React.FC<{
  label: string;
  description?: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}> = ({ label, description, value, onChange, disabled }) => (
  <div className="rounded-xl border border-stone-200 bg-white p-5">
    <label className="block text-base font-medium text-stone-900">{label}</label>
    {description && (
      <div className="text-sm text-stone-500 leading-snug mt-0.5 mb-3">{description}</div>
    )}
    <textarea
      value={value}
      disabled={disabled}
      onChange={e => onChange(e.target.value)}
      rows={4}
      className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400 disabled:bg-stone-50"
      placeholder="Write here..."
    />
  </div>
);

/**
 * Token doc carries cycleId + targetDocId (submission). The revieweeId is
 * not on the token directly; we need to surface it some other way.
 *
 * Phase-1 convention: token issuance puts the revieweeId at the start of
 * the submission id when the doc id was minted by the launcher
 * (`${cycleId}_${revieweeId}_${random}`). We extract it here. When the
 * launcher uses a flat random id instead, the submission doc itself stores
 * revieweeId (read after openSubmission), and this stub is only used for
 * the create call's revieweeId argument — which can be any value, since
 * the open path is idempotent on the existing doc.
 */
function deriveRevieweeFromToken(token: MagicLinkToken): string {
  const parts = token.targetDocId.split('_');
  if (parts.length >= 2) return parts[1];
  return '';
}

export default Review360MagicForm;
